// Storage usage helpers (QA-024 storage story).
//
// The v2 storage-usage endpoint (`GET /api/v2/storage/usage`, backend #1121)
// serves ZEROS until the byte-counting / backfill job is switched on. The page
// must treat that case honestly (counting-not-yet-on, NOT "you have no files"
// and NOT "something is broken"). These helpers normalise the response so the
// UI never has to guess whether a field arrived as raw bytes or a pre-formatted
// string, and never crashes on a missing / null field.

// BASE-10 (SI) units, matching the backend contract. The BE formats every
// storage string with humanfriendly.format_size() (base-10) — see
// utils/storage_usage_math.shape_storage_usage_payload ("base-10, like v1
// api/media/total-storage/"). The v1 Settings>Account meter renders those server
// strings verbatim. This formatter is what the Settings>Storage tab uses to turn
// raw *_bytes into labels, so it MUST use the SAME base or the two tabs disagree:
// measured live 2026-08-15 (gordon-21) the SAME 8,000,000,000-byte quota read
// "8 GB" on Account (server string) but "7.5 GB" on Storage (this formatter, when
// it divided by 1024^3). Base-10 makes 8e9 bytes -> "8 GB" on every surface.
const KB = 1000;
const MB = KB * 1000;
const GB = MB * 1000;

// Percentage thresholds that flip the usage bar's colour + tone.
export const STORAGE_WARN_PCT = 80; // amber: approaching limit
export const STORAGE_DANGER_PCT = 95; // red: at / over limit

/**
 * Coerce an unknown numeric-ish value to a finite number, else fallback.
 * Accepts numbers, numeric strings ("1048576"), and tolerates null/undefined.
 */
export const toNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") return fallback;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Human-readable bytes. 0 -> "0 MB" (we anchor small/empty accounts at MB so
 * the limit and the usage read in the same unit family). Negative -> "0 MB".
 */
export const formatBytes = (bytes, fractionDigits = 1) => {
  const n = toNumber(bytes, 0);
  if (n <= 0) return "0 MB";
  if (n < MB) {
    // Sub-MB still reads in MB to keep one consistent unit on the bar.
    return `${(n / MB).toFixed(fractionDigits)} MB`;
  }
  if (n < GB) {
    return `${stripZero((n / MB).toFixed(fractionDigits))} MB`;
  }
  return `${stripZero((n / GB).toFixed(fractionDigits))} GB`;
};

// "8.0" -> "8", "8.4" -> "8.4"
const stripZero = (str) => String(str).replace(/\.0$/, "");

/**
 * Clamp a raw percentage (number, or "12%" / "12" string) into 0..100.
 */
export const clampPercent = (value) => {
  let n = value;
  if (typeof n === "string") n = Number(n.replace("%", "").trim());
  n = toNumber(n, 0);
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
};

/**
 * Parse a raw percentage (number, "12", "12%") WITHOUT clamping, so an
 * over-quota value survives for display. Returns null when unusable.
 */
export const parsePercentUnclamped = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).replace("%", "").trim());
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
};

/**
 * Human percent LABEL (with the % sign) shown to the user. Kept to at most two
 * decimals — the same precision the backend rounds to (storage_usage_math:
 * round(...,2)) and the Settings>Account meter shows — so "0.41%" reads the same
 * on every surface. Critically it NEVER collapses a real, small usage to "0%":
 * measured live 2026-08-15 the Storage tab did `Math.round(0.41)` -> "0%" while
 * the Account tab showed "0.41%" for the identical account. 0 / garbage -> "0%".
 */
export const formatUsagePercent = (value) => {
  const n = parsePercentUnclamped(value);
  if (n === null || n === 0) return "0%";
  // Two decimals max, trailing zeros dropped: 0.41 -> "0.41", 0.4 -> "0.4",
  // 5 -> "5", 99.59 -> "99.59", 130 -> "130".
  const rounded = Math.round(n * 100) / 100;
  return `${rounded}%`;
};

/**
 * Derive a normalised view-model from whatever the API returned.
 *
 * Tolerates two response styles for the same fields:
 *   - raw bytes:     { storage_used: 1048576, storage_limit: 262144000, ... }
 *   - pre-formatted: { storage_used: "1 MB", storage_limit: "250 MB", ... }
 * If a *_bytes companion field exists we prefer it for maths.
 *
 * Returns null-safe fields the UI can render directly.
 */
export const normaliseUsage = (raw) => {
  const data = raw || {};

  const usedBytesReported = toNumber(
    data.storage_used_bytes ?? data.storage_used,
    0
  );
  const limitBytes = toNumber(
    data.storage_limit_bytes ?? data.storage_limit,
    0
  );

  // by-type breakdown -> normalised array, empty if the API doesn't send it.
  // Computed early: the headline total below falls back to this sum (ISP-storage-0mb —
  // measured on web-stg: headline read "0 MB used" while the breakdown table showed
  // "1.3 MB Images 100%" because `storage_used`/`storage_used_bytes` came back 0/absent
  // while `storage_by_type` carried the real bytes. The headline must reflect the same
  // total the breakdown table sums to, not a separate, unpopulated field).
  const byTypeRaw = data.storage_by_type || {};
  const breakdown = STORAGE_TYPES.map(({ key, label, color }) => ({
    key,
    label,
    color,
    bytes: toNumber(byTypeRaw[key], 0),
  })).filter((row) => row.bytes > 0);
  const breakdownBytes = breakdown.reduce((sum, row) => sum + row.bytes, 0);

  const usedBytes = usedBytesReported > 0 ? usedBytesReported : breakdownBytes;

  const remainingBytes = Math.max(
    0,
    toNumber(
      data.storage_remaining_bytes ?? data.storage_remaining,
      limitBytes - usedBytes
    )
  );

  // Prefer the server's percentage when present; otherwise compute it.
  let percent;
  if (
    data.storage_used_percentage !== undefined &&
    data.storage_used_percentage !== null &&
    data.storage_used_percentage !== ""
  ) {
    percent = clampPercent(data.storage_used_percentage);
  } else if (limitBytes > 0) {
    percent = clampPercent((usedBytes / limitBytes) * 100);
  } else {
    percent = 0;
  }

  const exceeded =
    data.storage_limit_exceeded === true ||
    (limitBytes > 0 && usedBytes > limitBytes);

  // TRUE, uncapped percentage — for the LABEL only, never for bar geometry.
  // `percent` is clamped to 0..100 because the bar cannot draw past full; using
  // that same clamped number in the text told an over-quota user "332.8 MB of
  // 256 MB (100%) used" (measured on web-stg 2026-07-28), which reads as
  // "exactly full" when they are 30% over. Bytes win when they disagree with a
  // server percentage the backend has already capped.
  const percentRaw = (() => {
    if (limitBytes > 0 && usedBytes > limitBytes) {
      return (usedBytes / limitBytes) * 100;
    }
    const reported = parsePercentUnclamped(data.storage_used_percentage);
    if (reported !== null) return reported;
    return percent;
  })();

  // "counting not yet on": the server is reachable (we have a limit) but every
  // usage signal is zero. This is the documented pre-backfill state.
  const countingPending =
    limitBytes >= 0 &&
    usedBytes === 0 &&
    breakdown.length === 0 &&
    data.counting_enabled !== true; // server may flip this flag later

  // SINGLE SOURCE OF TRUTH for the display labels. The backend already sends
  // base-10 human strings (storage_used / storage_limit / storage_remaining) —
  // the SAME strings the v1 endpoint and the Settings>Account meter render
  // verbatim. Prefer them so the Storage tab reads IDENTICALLY to the Account tab
  // instead of re-deriving a label that historically disagreed (measured live
  // 2026-08-15, gordon-21: Account "33.13 MB of 8 GB" vs Storage "31.6 MB of
  // 7.5 GB" for the identical bytes). Fall back to the (now base-10) formatBytes
  // only when a server string is absent — e.g. a byte-only payload, or the
  // pre-backfill 0-MB case where `usedBytes` is derived from the breakdown sum
  // (ISP-storage-0mb) and the server's headline string would read "0 B".
  const serverUsedLabel =
    typeof data.storage_used === "string" ? data.storage_used : null;
  const serverLimitLabel =
    typeof data.storage_limit === "string" ? data.storage_limit : null;
  const serverRemainingLabel =
    typeof data.storage_remaining === "string" ? data.storage_remaining : null;

  const usedLabel =
    usedBytesReported > 0 && serverUsedLabel
      ? serverUsedLabel
      : formatBytes(usedBytes);
  const limitLabel =
    limitBytes > 0 ? serverLimitLabel || formatBytes(limitBytes) : "—";
  const remainingLabel = serverRemainingLabel || formatBytes(remainingBytes);

  // T22.549: an Unlimited plan reports storage_limit_bytes 0 once counting is
  // on (v2 usage sends `storage_limit: "0 bytes"`). That rendered
  // "1.73 GB of — (0%) used" (measured web-stg main.cedc2a1f / 1fe63713, QA
  // ProSuite). No limit while usage IS being counted means "no cap", so say so.
  const unlimited = !countingPending && !(limitBytes > 0);

  return {
    usedBytes,
    limitBytes,
    remainingBytes,
    percent,
    percentRaw,
    percentLabel: formatUsagePercent(percentRaw),
    exceeded,
    breakdown,
    countingPending,
    usedLabel,
    limitLabel,
    remainingLabel,
    unlimited,
  };
};

/** Severity bucket for the bar colour + messaging. */
export const usageSeverity = ({ percent, exceeded }) => {
  if (exceeded || percent >= STORAGE_DANGER_PCT) return "danger";
  if (percent >= STORAGE_WARN_PCT) return "warn";
  return "ok";
};

export const isNearOrOverLimit = (severity) =>
  severity === "warn" || severity === "danger";

// Type rows in a stable display order, with the palette the breakdown uses.
// `key` matches the keys inside `storage_by_type` from the API.
export const STORAGE_TYPES = [
  { key: "images", label: "Images", color: "#9B5AF8" },
  { key: "videos", label: "Videos", color: "#26A840" },
  { key: "audios", label: "Audio", color: "#F79A00" },
  { key: "documents", label: "Documents", color: "#0F52BA" },
  { key: "others", label: "Other", color: "#6B6F80" },
];
